<template>
  <div class="learning-card-editor">
    <v-form ref="form">
      <v-text-field
        outlined
        color="brandPrimary"
        :label="titleLabel"
        :value="displayName"
        hide-details="auto"
        :rules="[
          (v) => !!v || 'A title is required',
          (v) => !v || v.length <= 255 || 'Title must be 255 characters or fewer',
        ]"
        @input="$emit('update:displayName', $event)"
      />

      <VueEditor
        :value="content"
        :editorToolbar="editorToolbar"
        :editorOptions="editorOptions"
        :placeholder="placeholder"
        class="learning-card-editor__richtext mt-4"
        useCustomImageHandler
        @image-added="handleImageAdded"
        @input="$emit('update:content', $event)"
      />
      <v-input :value="content" :rules="contentRules" hide-details="auto" class="ml-3" />
      <Typography variant="body-2" class="banner-description">
        Images are supported with a max file size of 10 MB.
      </Typography>
    </v-form>
    <LoadingDialog :showDialog="uploading" header="Uploading image..." />
  </div>
</template>

<script>
import { VueEditor } from "vue2-editor";
import { editorToolbarWithImages, editorOptionsWithImages } from "@/shared_data/data";
import ContentService from "@/services/ContentService";
import Typography from "@/components/generic/Typography.vue";
import LoadingDialog from "@/components/loadingDialog.vue";

const MAX_IMAGE_SIZE_MB = 10;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;

export default {
  name: "LearningCardEditor",
  components: {
    VueEditor,
    Typography,
    LoadingDialog,
  },
  props: {
    displayName: {
      type: String,
      default: "",
    },
    content: {
      type: String,
      default: "",
    },
    titleLabel: {
      type: String,
      default: "Title*",
    },
    placeholder: {
      type: String,
      default: "Enter content here...",
    },
    clientId: {
      type: [Number, String],
      default: null,
    },
    requireContent: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:displayName", "update:content"],
  data() {
    return {
      editorToolbar: editorToolbarWithImages,
      editorOptions: editorOptionsWithImages,
      uploading: false,
    };
  },
  computed: {
    contentRules() {
      if (!this.requireContent) return [];
      return [(v) => (!!v && this.stripHtml(v).trim().length > 0) || "Content is required"];
    },
  },
  methods: {
    validate() {
      return this.$refs.form.validate();
    },
    stripHtml(html) {
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || "";
    },
    async handleImageAdded(file, Editor, cursorLocation, resetUploader) {
      if (this.uploading) {
        this.$store.dispatch("showError", {
          message: "Please wait for the current image to finish uploading",
        });
        resetUploader?.();
        return;
      }

      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        this.$store.dispatch("showError", {
          message: `Image must be under ${MAX_IMAGE_SIZE_MB}MB`,
        });
        resetUploader?.();
        return;
      }

      this.uploading = true;
      try {
        const attachment = {
          name: "Learning Card Content",
          description: "Image used within the content of a learning card.",
          category: "LEARNING_CARD_CONTENT",
          clientId: this.clientId,
          file,
          generateSignedUrl: true,
          type: "image",
        };
        const response = await ContentService.createContent(attachment, 2);
        Editor.insertEmbed(cursorLocation, "image", response.contentUrlSigned);

        // Tag with contentId so uploadEmbeddedContent can normalize at save time
        const imgs = Editor.root.querySelectorAll("img:not([id])");
        for (const img of imgs) {
          if (img.src === response.contentUrlSigned) {
            img.setAttribute("id", `contentId-${response.contentId}`);
            break;
          }
        }
      } catch (e) {
        console.error("Failed to upload image", e);
        this.$store.dispatch("showError", {
          message: "Failed to upload image. Please try again.",
        });
      } finally {
        this.uploading = false;
        resetUploader?.();
      }
    },
  },
};
</script>

<style scoped>
.learning-card-editor {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
</style>
